import { useAuth, userManager } from '@/auth/AuthContext';
import { LOGIN_ERRORS, PATHS } from '@/constants';
import { LocationState } from '@/types/locationState';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../Loading/Loading';

// Module-level guard: React StrictMode remounts reset useRef, which can run
// signinCallback twice and briefly navigate to /error after a successful login.
let callbackInFlight: Promise<void> | null = null;

const LoginCallback = () => {
  const navigate = useNavigate();
  const { login, setSessionUser } = useAuth();

  useEffect(() => {
    if (callbackInFlight) {
      return;
    }

    const handleLoginCallback = async () => {
      try {
        const user = await userManager.signinCallback();

        // Sync React auth state before leaving /callback
        if (user) {
          setSessionUser(user);
        }

        const userState = user?.state as LocationState | undefined;
        const redirectUrl = userState?.redirectUrl;

        if (redirectUrl && typeof redirectUrl === 'string') {
          navigate(redirectUrl, { replace: true });
        } else {
          navigate(PATHS.APPLICANT.DASHBOARD, { replace: true });
        }
      } catch (error) {
        console.error('Login callback error:', error);

        // Duplicate callback (e.g. StrictMode) after a successful exchange
        const existingUser = await userManager.getUser();
        if (existingUser && !existingUser.expired) {
          setSessionUser(existingUser);
          navigate(PATHS.APPLICANT.DASHBOARD, { replace: true });
          return;
        }

        const errorMessage =
          error instanceof Error ? error.message : String(error);

        if (
          LOGIN_ERRORS.ACCESS.find((value) =>
            errorMessage.toLocaleLowerCase().includes(value)
          )
        ) {
          navigate('/', { replace: true });
          return;
        }

        if (
          LOGIN_ERRORS.STATE.find((value) =>
            errorMessage.toLocaleLowerCase().includes(value)
          )
        ) {
          try {
            await userManager.clearStaleState();
            login();
            navigate('/', { replace: true });
            return;
          } catch (clearError) {
            console.error('Failed to clear state and retry:', clearError);
          }
        }

        navigate(PATHS.UNAUTH.ERROR, { replace: true });
      }
    };

    callbackInFlight = handleLoginCallback().finally(() => {
      callbackInFlight = null;
    });
    // Run once per /callback visit; module guard covers StrictMode remounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <Loading />;
};

export default LoginCallback;
